import React, { useState, useEffect, useRef } from 'react';
import { Search, Menu, X, ChevronRight, ChevronDown, ChevronUp, Trash2, Recycle, Calendar, ExternalLink, Download } from 'lucide-react';
import { categories, events, quickLinks, communityStats } from '../data/communityData';
import EventIntakeForm from './EventIntakeForm';

const GenevaCommunity = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [expandedCards, setExpandedCards] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [isNavSticky, setIsNavSticky] = useState(false);
  const navRef = useRef(null);
  const categoryNavRef = useRef(null);
  const [isEventFormOpen, setIsEventFormOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Get current date for trash/recycling schedule
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
  
  // Calculate next trash pickup (Monday)
  const nextTrashDay = 1; // Monday
  const trashDate = new Date(today);
  const daysUntilTrash = (nextTrashDay + 7 - dayOfWeek) % 7;
  // If today is Monday but after pickup time, add 7 days
  if (dayOfWeek === 1 && today.getHours() >= 12) {
    trashDate.setDate(today.getDate() + 7);
  } else {
    trashDate.setDate(today.getDate() + daysUntilTrash);
  }
  
  // Set next recycling pickup to April 2nd, 2024
  const recyclingDate = new Date(2024, 3, 2); // Note: month is 0-indexed, so 3 = April
  
  // If today is after April 2nd, calculate the next recycling date (every other Wednesday)
  if (today > recyclingDate) {
    // Find the next recycling date after April 2nd
    const nextRecyclingDate = new Date(recyclingDate);
    
    // Since recycling is every other Wednesday, we need to adjust to the next Wednesday after April 2nd
    // and then continue adding 14 days
    const wednesday = 3; // 0 = Sunday, 3 = Wednesday
    
    // Calculate days until next Wednesday
    const daysUntilWednesday = (wednesday - nextRecyclingDate.getDay() + 7) % 7;
    
    // Adjust to the next Wednesday
    nextRecyclingDate.setDate(nextRecyclingDate.getDate() + daysUntilWednesday);
    
    // Then add 14 days until we find a future date
    while (nextRecyclingDate < today) {
      nextRecyclingDate.setDate(nextRecyclingDate.getDate() + 14);
    }
    
    recyclingDate.setTime(nextRecyclingDate.getTime());
  }

  // Add this function at the top of your component
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  // Then update the scroll handler useEffect
  useEffect(() => {
    const handleScrollImmediate = () => {
      if (navRef.current) {
        const scrollPosition = window.scrollY;
        const shouldBeSticky = scrollPosition > 10;
        
        if (isNavSticky !== shouldBeSticky) {
          setIsNavSticky(shouldBeSticky);
        }
      }
    };
    
    // Use debounced version for smoother performance
    const handleScroll = debounce(handleScrollImmediate, 10);

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initial check
    handleScrollImmediate();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isNavSticky]);

  // Add this useEffect to add a CSS class to the body when the component mounts
  useEffect(() => {
    // Add a class to prevent content jumps on mobile
    document.body.classList.add('overflow-x-hidden');
    
    return () => {
      document.body.classList.remove('overflow-x-hidden');
    };
  }, []);

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const toggleCardExpansion = (categoryName, subcategoryName, e) => {
    e.stopPropagation();
    const key = `${categoryName}-${subcategoryName}`;
    setExpandedCards(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const isCardExpanded = (categoryName, subcategoryName) => {
    const key = `${categoryName}-${subcategoryName}`;
    return expandedCards[key];
  };

  // Filter categories and subcategories based on search query
  const filteredCategories = searchQuery
    ? categories.map(category => ({
        ...category,
        subcategories: category.subcategories.filter(subcategory => {
          const matchesSubcategoryName = subcategory.name.toLowerCase().includes(searchQuery.toLowerCase());
          const matchesLinks = subcategory.links && subcategory.links.some(link => 
            link.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (link.services && link.services.toLowerCase().includes(searchQuery.toLowerCase()))
          );
          return matchesSubcategoryName || matchesLinks;
        })
      })).filter(category => category.subcategories.length > 0)
    : categories;

  // Scroll category nav to active category
  useEffect(() => {
    if (activeCategory && categoryNavRef.current) {
      const activeElement = categoryNavRef.current.querySelector(`[data-category="${activeCategory}"]`);
      if (activeElement) {
        categoryNavRef.current.scrollLeft = activeElement.offsetLeft - 20;
      }
    }
  }, [activeCategory]);

  const renderLinks = (links) => {
    return links.map((link, index) => (
      <a 
        key={index}
        href={link.url}
        target={link.isDownloadable ? "_self" : "_blank"} 
        rel={!link.isDownloadable ? "noopener noreferrer" : ""}
        download={link.isDownloadable}
        className="block p-4 hover:bg-gray-50 rounded-lg transition-colors border border-gray-100 mb-3"
      >
        <div className="flex justify-between items-start">
          <div>
            <h3 className="font-medium text-gray-900">{link.title}</h3>
            {link.services && <p className="text-sm text-gray-500 mt-1">{link.services}</p>}
            {link.contact && <p className="text-sm text-gray-500 mt-1">Contact: {link.contact}</p>}
          </div>
          <div className="flex items-center">
            {link.isDownloadable ? (
              <Download size={18} className="text-gray-400" />
            ) : (
              <ExternalLink size={18} className="text-gray-400" />
            )}
          </div>
        </div>
        {link.isDownloadable && link.fileType && (
          <div className="mt-2 flex items-center text-xs text-gray-500">
            <span className="bg-gray-100 rounded px-2 py-1 mr-2">{link.fileType}</span>
            {link.fileSize && <span>{link.fileSize}</span>}
          </div>
        )}
      </a>
    ));
  };

  const openEventRegistration = (event) => {
    console.log("Opening event registration for:", event);
    setSelectedEvent(event);
    setIsEventFormOpen(true);
  };

  const closeEventForm = () => {
    console.log("Closing event form");
    setIsEventFormOpen(false);
    setTimeout(() => {
      setSelectedEvent(null);
    }, 300);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with improved styling */}
      <header 
        ref={navRef} 
        className={`bg-gradient-to-r from-gray-900 to-gray-800 text-white 
          transition-all duration-300 will-change-transform
          ${isNavSticky ? 'sticky top-0 z-50 shadow-lg' : ''}`}
        style={{ 
          transform: isNavSticky ? 'translateZ(0)' : 'none',
          backfaceVisibility: 'hidden'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="md:hidden p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
              <h1 
                onClick={() => setActiveCategory(null)} 
                className="ml-2 text-2xl font-bold text-white cursor-pointer hover:text-white/90 transition-colors duration-200 flex items-center"
              >
                <span className="bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">Geneva</span>
                <span className="font-light ml-1">Community</span>
              </h1>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="relative hidden md:block">
                <input
                  type="text"
                  placeholder="Search resources..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-64 pl-10 pr-4 py-2 rounded-full border border-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent bg-gray-800/90 text-white placeholder-gray-400"
                />
                <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-2.5 text-gray-400 hover:text-white"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
            </div>
          </div>
          
          {/* Category Navigation - Horizontal Scrollable */}
          <div 
            ref={categoryNavRef}
            className="py-2 -mx-4 px-4 overflow-x-auto hide-scrollbar flex space-x-2 md:space-x-4 items-center"
          >
            <button
              onClick={() => setActiveCategory(null)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 flex items-center
                ${!activeCategory 
                  ? 'bg-white text-gray-900 shadow-md' 
                  : 'text-gray-300 hover:text-white hover:bg-gray-700/50'}`}
            >
              Home
            </button>
            
            {categories.map((category) => (
              <button
                key={category.name}
                data-category={category.name}
                onClick={() => setActiveCategory(category.name)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 flex items-center
                  ${activeCategory === category.name 
                    ? `bg-gradient-to-r ${category.color} text-white shadow-md` 
                    : 'text-gray-300 hover:text-white hover:bg-gray-700/50'}`}
              >
                {React.createElement(category.icon, { size: 16, className: 'mr-2' })}
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Mobile Search Bar */}
      <div className="md:hidden bg-gray-100 px-4 py-3 sticky top-0 z-40 shadow-sm">
        <div className="relative">
          <input
            type="text"
            placeholder="Search resources..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent bg-white"
          />
          <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
          {searchQuery && (
            <button 
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
            >
              <X size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar Navigation - Vertical for Desktop */}
          <nav className={`md:w-64 flex-shrink-0 ${isMenuOpen ? 'block' : 'hidden'} md:block`}>
            <div className="bg-white rounded-xl shadow-md p-4 sticky top-24">
              <h2 className="text-xl font-semibold mb-4 text-gray-900">Categories</h2>
              <div className="space-y-1">
                <button
                  onClick={() => setActiveCategory(null)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
                    ${!activeCategory 
                      ? 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-900' 
                      : 'text-gray-700 hover:bg-gray-100'}`}
                >
                  Home
                </button>
                
                {categories.map((category) => (
                  <button
                    key={category.name}
                    onClick={() => setActiveCategory(category.name)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center justify-between
                      ${activeCategory === category.name 
                        ? `bg-gradient-to-r ${category.color} text-white` 
                        : `text-gray-700 ${category.hoverBg}`}`}
                  >
                    <span className="flex items-center">
                      <span className={`p-1.5 rounded-md mr-2 ${category.iconBg}`}>
                        {React.createElement(category.icon, { size: 16, className: activeCategory === category.name ? 'text-white' : 'text-gray-700' })}
                      </span>
                      {category.name}
                    </span>
                    {activeCategory === category.name ? (
                      <ChevronDown size={16} />
                    ) : (
                      <ChevronRight size={16} />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </nav>

          {/* Main Content Area */}
          <div className="flex-1">
            {activeCategory ? (
              // Category content
              <div className="space-y-8">
                {filteredCategories
                  .find(category => category.name === activeCategory)
                  ?.subcategories.map((subcategory, subcategoryIndex) => {
                    const category = categories.find(c => c.name === activeCategory);
                    const subcategoryName = subcategory.name;
                    const isExpanded = isCardExpanded(activeCategory, subcategoryName);
                    const hasLinks = subcategory.links && subcategory.links.length > 0;
                    const hasContactInfo = subcategory.contactInfo && (typeof subcategory.contactInfo === 'string' || subcategory.contactInfo.length > 0);
                    const hasLongContent = hasLinks && subcategory.links.length > 3;
                    
                    return (
                      <div key={subcategoryIndex} className="bg-white rounded-xl shadow-md overflow-hidden">
                        <div className={`h-1 w-full bg-gradient-to-r ${category.color} transition-all duration-500 ${
                          isExpanded ? 'h-2' : 'group-hover:h-1.5'
                        }`}></div>
                        <div className="p-6">
                          <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {subcategoryName}
                            </h3>
                            {hasLongContent && (
                              <button 
                                onClick={(e) => {
                                  toggleCardExpansion(category.name, subcategoryName, e);
                                }}
                                className="flex items-center text-blue-600 hover:text-blue-800 focus:outline-none text-sm font-medium"
                                aria-label={isExpanded ? "Show less" : "Show more"}
                              >
                                {isExpanded ? (
                                  <>
                                    <span>Show Less</span>
                                    <ChevronUp size={16} className="ml-1" />
                                  </>
                                ) : (
                                  <>
                                    <span>Show More</span>
                                    <ChevronDown size={16} className="ml-1" />
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                          
                          {subcategory.description && (
                            <p className="text-gray-600 mb-4">{subcategory.description}</p>
                          )}
                          
                          {typeof subcategory.contactInfo === 'string' && subcategory.contactInfo && (
                            <div className="bg-blue-50 p-4 rounded-lg mb-4 text-blue-800 border border-blue-100">
                              <p className="font-medium">{subcategory.contactInfo}</p>
                            </div>
                          )}
                          
                          {/* Contact Info Array Rendering */}
                          {Array.isArray(subcategory.contactInfo) && subcategory.contactInfo.length > 0 && (
                            <div className="space-y-3 mb-4">
                              {(isExpanded || !hasLongContent || subcategory.contactInfo.length <= 3 
                                ? subcategory.contactInfo 
                                : subcategory.contactInfo.slice(0, 3)
                              ).map((contact, i) => (
                                <div key={i} className="bg-gray-50 p-4 rounded-lg border border-gray-100 hover:shadow-md transition-all duration-200">
                                  <div className="flex justify-between">
                                    <h4 className="font-medium text-gray-900">{contact.title}</h4>
                                    {contact.url && (
                                      <a 
                                        href={contact.url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-800 flex items-center text-sm"
                                      >
                                        <span>Visit</span>
                                        <ExternalLink size={14} className="ml-1" />
                                      </a>
                                    )}
                                  </div>
                                  {contact.contact && (
                                    <p className="text-sm text-gray-700 mt-1">
                                      <span className="font-medium">Contact:</span> {contact.contact}
                                    </p>
                                  )}
                                  {contact.services && (
                                    <p className="text-sm text-gray-600 mt-1">{contact.services}</p>
                                  )}
                                </div>
                              ))}
                              
                              {!isExpanded && hasLongContent && subcategory.contactInfo.length > 3 && (
                                <button 
                                  onClick={(e) => toggleCardExpansion(category.name, subcategoryName, e)}
                                  className="w-full py-2 text-sm text-blue-600 hover:text-blue-800 flex items-center justify-center"
                                >
                                  <span>Show {subcategory.contactInfo.length - 3} more</span>
                                  <ChevronDown size={14} className="ml-1" />
                                </button>
                              )}
                            </div>
                          )}
                          
                          {/* Links rendering */}
                          {hasLinks && (
                            <div className="space-y-3">
                              {(isExpanded || !hasLongContent || subcategory.links.length <= 3 ? subcategory.links : subcategory.links.slice(0, 3)).map((link, linkIndex) => (
                                <a 
                                  key={linkIndex}
                                  href={link.url}
                                  target={link.isDownloadable ? "_self" : "_blank"} 
                                  rel={!link.isDownloadable ? "noopener noreferrer" : ""}
                                  download={link.isDownloadable}
                                  className="block p-4 hover:bg-gray-50 rounded-lg transition-colors border border-gray-100 mb-3"
                                >
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <h3 className="font-medium text-gray-900">{link.title}</h3>
                                      {link.services && <p className="text-sm text-gray-500 mt-1">{link.services}</p>}
                                      {link.contact && <p className="text-sm text-gray-500 mt-1">Contact: {link.contact}</p>}
                                    </div>
                                    <div className="flex items-center">
                                      {link.isDownloadable ? (
                                        <Download size={18} className="text-gray-400" />
                                      ) : (
                                        <ExternalLink size={18} className="text-gray-400" />
                                      )}
                                    </div>
                                  </div>
                                  {link.isDownloadable && link.fileType && (
                                    <div className="mt-2 flex items-center text-xs text-gray-500">
                                      <span className="bg-gray-100 rounded px-2 py-1 mr-2">{link.fileType}</span>
                                      {link.fileSize && <span>{link.fileSize}</span>}
                                    </div>
                                  )}
                                </a>
                              ))}
                              
                              {hasLongContent && subcategory.links.length > 3 && !isExpanded && (
                                <button 
                                  onClick={(e) => toggleCardExpansion(category.name, subcategory.name, e)}
                                  className="w-full text-center py-2 text-blue-500 hover:text-blue-700 font-medium text-sm flex items-center justify-center"
                                >
                                  <span>Show {subcategory.links.length - 3} more</span>
                                  <ChevronDown size={16} className="ml-1" />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                }
              </div>
            ) : (
              // Home content
              <div className="space-y-8">
                {/* Welcome Banner */}
                <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl shadow-lg p-8 text-white">
                  <h2 className="text-3xl font-bold mb-4">Welcome to Geneva Community</h2>
                  <p className="text-xl text-white/90 mb-6">Your one-stop resource for everything in our neighborhood</p>
                  <div className="flex flex-wrap gap-4">
                    {quickLinks.map((link, index) => (
                      <a
                        key={index}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${link.color} text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:opacity-90 transition-opacity shadow-sm hover:shadow-md`}
                      >
                        {React.createElement(link.icon, { size: 20 })}
                        <span>{link.title}</span>
                      </a>
                    ))}
                  </div>
                </div>

                {/* Trash & Recycling Pickup Schedule */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Trash Pickup Card */}
                  <div className="bg-white rounded-xl shadow-md overflow-hidden group hover:shadow-lg transition-all duration-300">
                    <div className="h-2 w-full bg-gradient-to-r from-green-500 to-emerald-500 group-hover:h-3 transition-all duration-300"></div>
                    <div className="p-6">
                      <div className="flex items-center mb-4">
                        <div className="p-3 bg-green-100 rounded-full mr-4">
                          <Trash2 size={24} className="text-green-600" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900">Trash Pickup</h3>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-600 mb-1">Next pickup:</p>
                          <p className="text-2xl font-bold text-gray-800">{formatDate(trashDate)}</p>
                          <p className="text-sm text-gray-500 mt-1">Schedule: Every Monday</p>
                        </div>
                        <div className="bg-green-50 p-3 rounded-full flex items-center justify-center h-16 w-16">
                          <Calendar size={28} className="text-green-600" />
                        </div>
                      </div>
                      
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Reminder:</span> Place bins at curb by 7:00 AM on pickup day
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Recycling Pickup Card */}
                  <div className="bg-white rounded-xl shadow-md overflow-hidden group hover:shadow-lg transition-all duration-300">
                    <div className="h-2 w-full bg-gradient-to-r from-blue-500 to-cyan-500 group-hover:h-3 transition-all duration-300"></div>
                    <div className="p-6">
                      <div className="flex items-center mb-4">
                        <div className="p-3 bg-blue-100 rounded-full mr-4">
                          <Recycle size={24} className="text-blue-600" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900">Recycling Pickup</h3>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-600 mb-1">Next pickup:</p>
                          <p className="text-2xl font-bold text-gray-800">{formatDate(recyclingDate)}</p>
                          <p className="text-sm text-gray-500 mt-1">Schedule: Every other Wednesday</p>
                        </div>
                        <div className="bg-blue-50 p-3 rounded-full flex items-center justify-center h-16 w-16">
                          <Calendar size={28} className="text-blue-600" />
                        </div>
                      </div>
                      
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Accepted items:</span> Paper, cardboard, plastic bottles, aluminum cans, glass
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Community Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {communityStats.map((stat) => (
                    <div key={stat.label} className={`bg-gradient-to-r ${stat.color} rounded-xl shadow-md p-6 text-white transform transition-all duration-300 hover:shadow-lg`}>
                      <h3 className="text-4xl font-bold mb-2">{stat.value}</h3>
                      <p className="text-white/90">{stat.label}</p>
                    </div>
                  ))}
                </div>

                {/* Upcoming Events */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                  <div className="h-2 w-full bg-gradient-to-r from-purple-500 to-pink-500"></div>
                  <div className="p-6">
                    <h2 className="text-xl font-semibold mb-4 text-gray-900 flex items-center">
                      <Calendar size={20} className="mr-2 text-purple-500" />
                      Upcoming Community Events
                    </h2>
                    <div className="space-y-4">
                      {events.map((event, index) => (
                        <div key={index} className="border-b border-gray-100 last:border-0 pb-4 last:pb-0">
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="font-semibold text-gray-900">{event.title}</h4>
                              <p className="text-gray-600">
                                {event.date} • {event.time} • {event.location}
                              </p>
                            </div>
                            <button
                              onClick={() => openEventRegistration(event)}
                              className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium hover:bg-purple-200 transition-colors"
                            >
                              Register
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Footer with improved styling */}
      <footer className="bg-gradient-to-r from-gray-900 to-gray-800 text-white mt-12 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <h2 className="text-xl font-bold">
                <span className="bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">Geneva</span>
                <span className="font-light">Community</span>
              </h2>
              <p className="text-gray-400 text-sm mt-1">Your neighborhood resource hub</p>
            </div>
            <div className="flex space-x-6">
              <a href="/privacy" className="text-gray-300 hover:text-white transition-colors">Privacy Policy</a>
              <a href="/terms" className="text-gray-300 hover:text-white transition-colors">Terms of Service</a>
              <a href="/contact" className="text-gray-300 hover:text-white transition-colors">Contact</a>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-gray-700 text-center text-gray-400 text-sm">
            © {new Date().getFullYear()} Geneva Community. All rights reserved.
          </div>
        </div>
      </footer>

      {/* Event Registration Form */}
      {isEventFormOpen && (
        <EventIntakeForm 
          isOpen={isEventFormOpen}
          onClose={closeEventForm}
          eventTitle={selectedEvent?.title || ''}
        />
      )}
    </div>
  );
};

export default GenevaCommunity; 